/**
 * HEIC / HEIF preview support for the photo-upload slot.
 *
 * ═══ THE BUG THIS FIXES ═══════════════════════════════════════════════
 *
 * Customer picks a HEIC photo (default iPhone camera format). PWA
 * previously called `URL.createObjectURL(file)` and passed the raw
 * HEIC blob straight to `<img src>`. Non-Safari browsers (Chrome,
 * Firefox, Edge, Android WebView) cannot decode HEIC natively and
 * render a broken-image icon or an empty slot. Customer reads that
 * as "the upload failed" and abandons.
 *
 * ═══ FOUR-RUNG APPROACH (Sebastian ruling 2026-09-23, final) ══════════
 *
 *   1. Not HEIC → existing `URL.createObjectURL(file)` (no change).
 *   2. HEIC + `createImageBitmap(file)` resolves → use it. Safari
 *      decodes HEIC natively via the OS codec. Capability-tested per
 *      file (do not sniff the browser); iPhone customers in Safari
 *      never download a decoder at all.
 *   3. HEIC + native decode throws → lazy-load a Web Worker running
 *      libheif-js and decode there. `libheif-js/wasm-bundle` = the
 *      exact version the backend depends on (v1.19.8, upstream
 *      libheif v1.23.2 with libde265 HEVC decoder). Loaded from our
 *      own `node_modules` — never a CDN.
 *   4. Anything fails or the 5-second hard timeout fires → fall
 *      through to the calm `heic` slot state. Submission still works
 *      — the ORIGINAL File goes to GCS untouched and the backend
 *      sanitizer does the authoritative conversion. A failed preview
 *      never blocks the order.
 *
 * ═══ WHY NOT heic2any? ════════════════════════════════════════════════
 *
 * heic2any@0.0.4 (last release 2019) bundles its own libheif build
 * that has NO HEVC decoder (verified — zero `hevc`, `hvc1`, `libde265`
 * strings in its 1.4 MB bundle). Real iPhone HEICs are HEVC-encoded,
 * so heic2any fails at parse with "Could not parse HEIF file". Our
 * libheif-js@1.19.8/wasm-bundle contains the full libde265 HEVC
 * decoder and decodes the same file cleanly (verified server-side
 * 2026-09-23, and in-browser against IMG_4716.HEIC same day: parse
 * 7 ms, full decode 358 ms, total 395 ms on MacBook desktop).
 *
 * ═══ MAGIC BYTE DETECTION ═════════════════════════════════════════════
 *
 * `file.type` alone is NOT reliable: iOS Files-app sometimes reports
 * an empty MIME on a HEIC pick, and browsers vary. The backend
 * sanitizer already reads the ISO Base Media `ftyp` box at bytes 4-11
 * (see `src/photos-safety/photo-sanitizer.service.ts:362-377`). This
 * helper uses the same 7-brand allow-list so the client-side detection
 * agrees with the server-side detection by construction.
 */

/**
 * ISO Base Media / HEIF `ftyp` major brands that indicate a HEIC-
 * family image. Mirrors the backend allow-list at
 * `src/photos-safety/photo-sanitizer.service.ts:369-377`.
 */
const HEIC_BRANDS: ReadonlySet<string> = new Set([
  'heic',
  'heix',
  'heif',
  'mif1',
  'msf1',
  'hevc',
  'hevx',
]);

/**
 * Byte-level detection: read the first 12 bytes and check for a
 * `ftyp` box at offset 4 with a HEIC-family brand at offset 8.
 * Returns false on any shorter buffer, on a non-`ftyp` box, or on a
 * brand outside the allow-list.
 */
export function isHeicMagicBytes(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  // ASCII 'f' 't' 'y' 'p' at bytes 4-7.
  if (bytes[4] !== 0x66 || bytes[5] !== 0x74 || bytes[6] !== 0x79 || bytes[7] !== 0x70) {
    return false;
  }
  // Non-null assertion is safe: length check above guarantees indices
  // 8-11 exist.
  const brand = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!);
  return HEIC_BRANDS.has(brand);
}

/**
 * Decide whether a File is HEIC / HEIF and therefore should route to
 * the calm HEIC confirmation slot state instead of a preview.
 *
 * Combines the `file.type` hint with a magic-byte sniff for the
 * empty-MIME iOS Files-app edge case. Reads only the first 12 bytes
 * via `File.slice(0, 12).arrayBuffer()` on the empty-MIME path —
 * cheap on any file size, no full read.
 */
export async function needsHeicPreviewConversion(file: File): Promise<boolean> {
  const type = file.type.toLowerCase();
  if (
    type === 'image/heic' ||
    type === 'image/heif' ||
    type === 'image/heic-sequence' ||
    type === 'image/heif-sequence'
  ) {
    return true;
  }
  // Non-HEIC MIME with contents: trust the type and skip the sniff.
  // The magic-byte fallback ONLY applies to the empty-type case
  // (iOS Files-app HEIC pick) so we do not read bytes on the
  // majority path.
  if (type !== '' && type.startsWith('image/')) return false;
  try {
    const head = await file.slice(0, 12).arrayBuffer();
    return isHeicMagicBytes(new Uint8Array(head));
  } catch {
    return false;
  }
}

/**
 * Hard timeout for HEIC preview conversion (Sebastian ruling
 * 2026-09-23, final). At 358 ms desktop / ~1.5 s throttled mobile
 * for a 12 MP iPhone HEIC, anything past 5 s means something is
 * wrong. The calm-confirmation fallback is preferable to leaving
 * the customer staring at a spinner.
 */
const HEIC_PREVIEW_TIMEOUT_MS = 5000;

/**
 * Response shape emitted by `heic-preview.worker.ts`. Kept in sync
 * with the worker's own union — the orchestrator handles both arms.
 */
type WorkerResponse =
  | { readonly ok: true; readonly blob: Blob }
  | {
      readonly ok: false;
      readonly reason: 'parse_failed' | 'too_large' | 'decode_failed';
      readonly detail?: string;
    };

/**
 * Result of `previewHeicInBrowser`. The success arm carries a JPEG
 * Blob that the caller pipes into `URL.createObjectURL` for the
 * `<img>` element. The failure arm names the reason so the caller
 * can log + route to the calm-confirmation slot state.
 */
export type HeicPreviewOutcome =
  | { readonly ok: true; readonly blob: Blob; readonly source: 'native' | 'worker' }
  | { readonly ok: false; readonly reason: HeicPreviewFailure };

export type HeicPreviewFailure =
  | 'native_and_worker_failed'
  | 'timeout'
  | 'too_large'
  | 'unsupported';

/**
 * Try to produce a preview-renderable Blob for a HEIC file. Order:
 *
 *   1. `createImageBitmap(file)` — Safari's native HEIC decoder.
 *      Returns a bitmap immediately if the browser can handle HEIC
 *      (iPhone Safari, macOS Safari). Rejected instantly on
 *      Chrome/Firefox/Android with `InvalidStateError` — no
 *      main-thread cost, no worker spun up.
 *
 *   2. Web Worker running `libheif-js/wasm-bundle`. Lazy-imported
 *      via Vite's `?worker` suffix so the ~509 KB gzipped bundle
 *      only reaches customers who both pick a HEIC AND lack native
 *      decode. Worker is terminated after every conversion —
 *      wasm heap released, memory returns to baseline between picks.
 *
 * Throws never. Failures are named in the `HeicPreviewOutcome`
 * discriminated union so the caller can route to the calm HEIC
 * slot state without a try/catch dance.
 */
export async function previewHeicInBrowser(file: File): Promise<HeicPreviewOutcome> {
  // ── Rung 2 — native `createImageBitmap` ─────────────────────
  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
      const targetW = Math.max(1, Math.round(bitmap.width * scale));
      const targetH = Math.max(1, Math.round(bitmap.height * scale));
      const target = new OffscreenCanvas(targetW, targetH);
      const ctx = target.getContext('2d');
      if (ctx === null) throw new Error('no 2d context');
      ctx.drawImage(bitmap, 0, 0, targetW, targetH);
      const blob = await target.convertToBlob({ type: 'image/jpeg', quality: 0.8 });
      return { ok: true, blob, source: 'native' };
    } finally {
      bitmap.close?.();
    }
  } catch {
    // Expected on non-Safari for HEIC — fall through to worker rung.
  }

  // ── Rung 3 — Web Worker + libheif-js ────────────────────────
  let worker: Worker | null = null;
  try {
    // Dynamic import: Vite emits a separate chunk containing the
    // worker + libheif-js/wasm-bundle. Chunk fetch happens once
    // per session on first HEIC pick that reaches this rung.
    const workerModule = await import('./heic-preview.worker?worker');
    const WorkerCtor = workerModule.default;
    worker = new WorkerCtor();
    const bytes = await file.arrayBuffer();

    const outcome = await new Promise<WorkerResponse | 'timeout'>((resolve) => {
      const timeoutHandle = setTimeout(() => resolve('timeout'), HEIC_PREVIEW_TIMEOUT_MS);
      worker!.addEventListener(
        'message',
        (event: MessageEvent<WorkerResponse>) => {
          clearTimeout(timeoutHandle);
          resolve(event.data);
        },
        { once: true },
      );
      worker!.addEventListener(
        'error',
        (event: ErrorEvent) => {
          clearTimeout(timeoutHandle);
          resolve({
            ok: false,
            reason: 'decode_failed',
            detail: `worker error: ${event.message || 'unknown'}`,
          });
        },
        { once: true },
      );
      // Transfer ownership of the ArrayBuffer → zero-copy hand-off.
      worker!.postMessage({ bytes }, [bytes]);
    });

    if (outcome === 'timeout') return { ok: false, reason: 'timeout' };
    if (outcome.ok) return { ok: true, blob: outcome.blob, source: 'worker' };
    if (outcome.reason === 'too_large') return { ok: false, reason: 'too_large' };
    return { ok: false, reason: 'native_and_worker_failed' };
  } catch {
    return { ok: false, reason: 'unsupported' };
  } finally {
    // Nuclear release of the wasm heap + module — memory returns to
    // baseline between HEIC picks. Sebastian ruling 2026-09-23:
    // "terminate the worker after each conversion".
    worker?.terminate();
  }
}
